"use client";

import { AnalyticsSkeleton, ContentSkeleton } from "@/components/ui/skeleton";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { RefreshCcwIcon, SparklesIcon, SearchIcon, GlobeIcon, BrainIcon, DatabaseIcon, TerminalIcon, PenTool, WrenchIcon, CalendarIcon, CheckIcon, CopyIcon, DownloadIcon } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { fmtCost } from "@/lib/format-cost";
import { MessageResponse } from "@/components/ai-elements/message";

// ─── Types ────────────────────────────────────────────────────────────────────

type Range = "15m" | "30m" | "1h" | "3h" | "12h" | "24h" | "7d" | "custom";

type TokenChat = {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string | null;
  created_at: string;
  updated_at: string;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  pricing_snapshot: Record<string, unknown> | null;
  agent_name: string | null;
  tool_names: string[] | null;
  message_count: number;
  /** Persisted cost (Phase 1/5/7) — token cost + tool cost, summed
   * server-side in get-token-analytics. Null when nothing here is priced. */
  cost: number | null;
  tool_cost: number | null;
};

type ApiResponse = {
  range: Range;
  chats: TokenChat[];
};

type MessageTokenPoint = {
  label: string;
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
};

type Props = { onBack: () => void };

// ─── Constants ────────────────────────────────────────────────────────────────

const RANGES: { value: Range; label: string }[] = [
  { value: "15m",  label: "15 min" },
  { value: "30m",  label: "30 min" },
  { value: "1h",   label: "1 h" },
  { value: "3h",   label: "3 h" },
  { value: "12h",  label: "12 h" },
  { value: "24h",  label: "24 h" },
  { value: "7d",   label: "7 d" },
];

/** Minutes represented by each range */
const RANGE_MINUTES: Record<Range, number> = {
  "15m": 15,
  "30m": 30,
  "1h": 60,
  "3h": 180,
  "12h": 720,
  "24h": 1440,
  "7d": 10080,
  "custom": 1440, // fallback for custom — overridden by actual date range
};

/** Bucket size in minutes for each range (targets ~15-20 x-axis points) */
const BUCKET_MINUTES: Record<Range, number> = {
  "15m": 1,
  "30m": 2,
  "1h": 5,
  "3h": 15,
  "12h": 60,
  "24h": 120,
  "7d": 720,
  "custom": 120, // fallback for custom
};

// Max chats shown in the stacked area (top by tokens after filter)
const MAX_CHART_CHATS = 30;

// ─── Colour palette ───────────────────────────────────────────────────────────

const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

function chatColor(index: number): string {
  // Cycle through 5 chart tokens, then shift hue for extra chats
  if (index < CHART_COLORS.length) return CHART_COLORS[index];
  const hue = (index * 47) % 360; // pseudo-random golden-angle spread
  return `hsl(${hue}, 65%, 55%)`;
}

// Returns a stable HSL color for a user badge (always hsl(...) so we can create alpha variants)
function userBadgeColor(index: number): string {
  const presets = ["210,75%,55%", "142,60%,40%", "38,90%,48%", "262,65%,60%", "338,75%,55%"];
  if (index < presets.length) return `hsl(${presets[index]})`;
  const hue = (index * 137.5) % 360; // golden angle for good distribution
  return `hsl(${hue}, 65%, 52%)`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/**
 * Persisted cost (Phase 7) — token cost + tool cost, computed once at write
 * time and summed server-side, not re-estimated here. Null when neither is
 * priced, so an unpriced chat renders as unavailable rather than $0.
 */
function chatCost(chat: TokenChat): number | null {
  if (chat.cost === null && chat.tool_cost === null) return null;
  return (chat.cost ?? 0) + (chat.tool_cost ?? 0);
}

function buildTimeBuckets(range: Range, customDateRange?: DateRange): Date[] {
  let startMs: number;
  let endMs: number;
  let bucketMs: number;

  if (range === "custom" && customDateRange?.from) {
    startMs = customDateRange.from.getTime();
    const toDate = customDateRange.to ?? customDateRange.from;
    // Use end-of-day for the `to` date
    const end = new Date(toDate);
    end.setHours(23, 59, 59, 999);
    endMs = end.getTime();
    // Compute a sensible bucket size: aim for ~15 ticks
    const totalMins = (endMs - startMs) / 60000;
    const targetTicks = 15;
    const rawBucket = totalMins / targetTicks;
    // Round up to a "nice" number
    const niceSteps = [1, 2, 5, 10, 15, 30, 60, 120, 240, 360, 720, 1440, 2880, 10080];
    bucketMs = (niceSteps.find((s) => s >= rawBucket) ?? niceSteps[niceSteps.length - 1]) * 60 * 1000;
  } else {
    const totalMs = RANGE_MINUTES[range] * 60 * 1000;
    endMs = Date.now();
    startMs = endMs - totalMs;
    bucketMs = BUCKET_MINUTES[range] * 60 * 1000;
  }

  const buckets: Date[] = [];
  for (let t = startMs; t <= endMs; t += bucketMs) {
    buckets.push(new Date(t));
  }
  // Always include the end
  if (buckets[buckets.length - 1].getTime() < endMs - bucketMs / 2) {
    buckets.push(new Date(endMs));
  }
  return buckets;
}

function fmtBucketTime(d: Date, range: Range, customDateRange?: DateRange): string {
  if (range === "custom" && customDateRange?.from) {
    const spanDays = customDateRange.to
      ? (customDateRange.to.getTime() - customDateRange.from.getTime()) / 86400000
      : 0;
    if (spanDays > 2) {
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    }
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
  }
  if (range === "7d") {
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  }
  if (range === "24h" || range === "12h") {
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Linear interpolation of a chat's tokens at a given time */
function chatTokensAt(chat: TokenChat, t: Date): number {
  const ts = t.getTime();
  const created = new Date(chat.created_at).getTime();
  const updated = new Date(chat.updated_at).getTime();
  if (ts < created) return 0;
  if (ts >= updated || updated === created) return chat.total_tokens;
  return Math.round(chat.total_tokens * (ts - created) / (updated - created));
}

// ─── Histogram buckets ────────────────────────────────────────────────────────

const HIST_BOUNDARIES = [0, 500, 1_000, 5_000, 10_000, 50_000, 100_000, 500_000, Infinity];

function histLabel(low: number, high: number): string {
  const fmt = (n: number) => (n >= 1_000_000 ? `${n / 1_000_000}M` : n >= 1_000 ? `${n / 1_000}k` : String(n));
  if (high === Infinity) return `>${fmt(low)}`;
  return `${fmt(low)}–${fmt(high)}`;
}

function buildHistogram(chats: TokenChat[]): Array<{ label: string; count: number; low: number; high: number }> {
  return HIST_BOUNDARIES.slice(0, -1).map((low, i) => {
    const high = HIST_BOUNDARIES[i + 1];
    return {
      label: histLabel(low, high),
      count: chats.filter((c) => c.total_tokens >= low && c.total_tokens < high).length,
      low,
      high,
    };
  });
}

// ─── Tool icon map ────────────────────────────────────────────────────────────
// Maps tool display names (as returned by the edge function) to icons + colours.

type ToolIconDef = { Icon: React.ComponentType<{ className?: string }>; color: string; label: string };

const TOOL_ICONS: Record<string, ToolIconDef> = {
  "Web Search":    { Icon: SearchIcon,   color: "text-gray-500",   label: "Web Search" },
  "Web Fetch":     { Icon: GlobeIcon,    color: "text-gray-500",   label: "Web Fetch" },
  "Memory":        { Icon: BrainIcon,    color: "text-gray-500", label: "Memory" },
  "Knowledge Base":{ Icon: DatabaseIcon, color: "text-gray-500",  label: "Knowledge Base" },
  "Code Execution":{ Icon: TerminalIcon, color: "text-gray-500",  label: "Code Execution" },
  "Writing Style": { Icon: PenTool,  color: "text-gray-500",   label: "Writing Style" },
};

function toolIconDef(name: string): ToolIconDef {
  return TOOL_ICONS[name] ?? { Icon: WrenchIcon, color: "text-muted-foreground", label: name };
}


export function TokenAnalyticsPanel({ onBack }: Props) {
  const [range, setRange] = useState<Range>("1h");
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Custom date range picker state
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Token range: brush selection as [startIndex, endIndex] into histogram array.
  // null = no selection (all buckets included).
  const [brushIndices, setBrushIndices] = useState<[number, number] | null>(null);
  // Set of chat IDs explicitly hidden via legend toggle
  const [hiddenChats, setHiddenChats] = useState<Set<string>>(new Set());

  // ── Reliability analysis sheet ──────────────────────────────────────────────
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisChat, setAnalysisChat] = useState<TokenChat | null>(null);
  const [analysisText, setAnalysisText] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const analysisAbortRef = useRef<AbortController | null>(null);

  // ── Message token detail sheet ─────────────────────────────────────────────
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailChat, setDetailChat] = useState<TokenChat | null>(null);
  const [detailMessages, setDetailMessages] = useState<MessageTokenPoint[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = useCallback(async (chat: TokenChat) => {
    setDetailChat(chat);
    setDetailMessages([]);
    setDetailLoading(true);
    setDetailOpen(true);

    type MsgRow = {
      sequence_number: number;
      created_at: string;
      token_usage: {
        total: { input: number; output: number; cache_read: number; cache_write: number };
      } | null;
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-token-analytics-messages?chat_id=${encodeURIComponent(chat.id)}`;
      const res = await fetch(url, {
        headers: {
          apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load message token detail.");

      const points: MessageTokenPoint[] = ((json.messages ?? []) as MsgRow[]).map((m) => ({
        label: new Date(m.created_at).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
        input: m.token_usage?.total.input ?? 0,
        output: m.token_usage?.total.output ?? 0,
        cache_read: m.token_usage?.total.cache_read ?? 0,
        cache_write: m.token_usage?.total.cache_write ?? 0,
      }));

      setDetailMessages(points);
    } catch {
      setDetailMessages([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Sheet width (drag-to-resize)
  const [sheetWidth, setSheetWidth] = useState(680);
  const isDragging = useRef(false);
  const [copied, setCopied] = useState(false);

  const handleResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const newWidth = window.innerWidth - e.clientX;
    setSheetWidth(Math.min(Math.max(newWidth, 360), window.innerWidth - 80));
  }, []);

  const handleResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  const handleCopyAnalysis = useCallback(() => {
    if (!analysisText) return;
    void navigator.clipboard.writeText(analysisText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [analysisText]);

  const handleDownloadAnalysis = useCallback(() => {
    if (!analysisText || !analysisChat) return;
    const blob = new Blob([analysisText], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reliability-${analysisChat.id.slice(0, 8)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [analysisText, analysisChat]);

  const runAnalysis = useCallback(async (chat: TokenChat) => {
    // Abort any previous in-flight stream
    analysisAbortRef.current?.abort();
    const controller = new AbortController();
    analysisAbortRef.current = controller;

    setAnalysisChat(chat);
    setAnalysisText("");
    setAnalysisError(null);
    setAnalysisLoading(true);
    setAnalysisOpen(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/analyse-chat-reliability", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ chatId: chat.id }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Request failed (${res.status})`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setAnalysisText((prev) => prev + decoder.decode(value, { stream: true }));
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setAnalysisError(err instanceof Error ? err.message : "Analysis failed.");
    } finally {
      setAnalysisLoading(false);
    }
  }, []);

  // ── Data fetch ──────────────────────────────────────────────────────────────

  const load = useCallback(async (r: Range, dateRange?: DateRange) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-token-analytics`;
      if (r === "custom" && dateRange?.from) {
        const from = dateRange.from.toISOString();
        const to = (dateRange.to ?? dateRange.from).toISOString();
        url += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      } else {
        url += `?range=${r}`;
      }
      const res = await fetch(url, {
        headers: {
          apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load token analytics.");
      setData(json as ApiResponse);
      // Reset brush when data refreshes
      setBrushIndices(null);
      setHiddenChats(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load token analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (range === "custom") return; // only load custom when user explicitly submits
    load(range);
  }, [load, range]);

  // ── Derived: token range from slider (percentage → actual tokens) ───────────

  const allChats = useMemo(() => data?.chats ?? [], [data?.chats]);
  const _maxTokens = useMemo(() => Math.max(...allChats.map((c) => c.total_tokens), 1), [allChats]);

  // Derive token min/max from brush selection (bucket boundaries) or use full range
  const histogram = useMemo(() => buildHistogram(allChats), [allChats]);

  // Chats within the brush range, sorted by total_tokens descending
  const filteredChats = useMemo(() => {
    const tokenMin = brushIndices ? histogram[brushIndices[0]].low : 0;
    const tokenMax = brushIndices
      ? histogram[brushIndices[1]].high === Infinity
        ? Infinity
        : histogram[brushIndices[1]].high
      : Infinity;
    return allChats
      .filter((c) => c.total_tokens >= tokenMin && (tokenMax === Infinity || c.total_tokens < tokenMax))
      .sort((a, b) => b.total_tokens - a.total_tokens);
  }, [allChats, brushIndices, histogram]);

  // Derived display values for the range badge (computed after filteredChats so histogram is ready)
  const tokenMin = brushIndices ? histogram[brushIndices[0]].low : 0;
  const tokenMax = brushIndices
    ? histogram[brushIndices[1]].high === Infinity
      ? Infinity
      : histogram[brushIndices[1]].high
    : Infinity;

  // Chats to show in the area chart (top MAX_CHART_CHATS, minus explicitly hidden)
  const chartChats = useMemo(
    () =>
      filteredChats
        .filter((c) => !hiddenChats.has(c.id))
        .slice(0, MAX_CHART_CHATS),
    [filteredChats, hiddenChats]
  );

  // Assign stable colour indices by position in filteredChats list
  const colorMap = useMemo(() => {
    const m = new Map<string, string>();
    filteredChats.forEach((c, i) => m.set(c.id, chatColor(i)));
    return m;
  }, [filteredChats]);

  // Assign stable badge colours per unique user (same user → same colour across rows)
  const userColorMap = useMemo(() => {
    const m = new Map<string, string>();
    filteredChats.forEach((c) => {
      if (!m.has(c.user_id)) m.set(c.user_id, userBadgeColor(m.size));
    });
    return m;
  }, [filteredChats]);

  // ── Derived: time-series chart data ─────────────────────────────────────────

  const timeBuckets = useMemo(() => buildTimeBuckets(range, customDateRange), [range, customDateRange]);

  const chartData = useMemo(() => {
    return timeBuckets.map((bucket) => {
      const row: Record<string, number | string> = {
        time: fmtBucketTime(bucket, range, customDateRange),
        _ts: bucket.getTime(),
      };
      let runningTotal = 0;
      for (const chat of chartChats) {
        const t = chatTokensAt(chat, bucket);
        row[chat.id] = t;
        runningTotal += t;
      }
      row._total = runningTotal;
      return row;
    });
  }, [timeBuckets, chartChats, range, customDateRange]);

  // Stable rank label: position within filteredChats (tokens-desc), 1-based
  const rankMap = useMemo(() => {
    const m = new Map<string, number>();
    filteredChats.forEach((c, i) => m.set(c.id, i + 1));
    return m;
  }, [filteredChats]);
  const chatLabel = (c: TokenChat) => `Chat #${rankMap.get(c.id) ?? "?"}`;

  // Build recharts ChartConfig for the area chart.
  // Uses colorMap (filteredChats-stable positions) so colors don't shift when items are hidden.
  const areaChartConfig = useMemo(() => {
    const config: ChartConfig = {};
    chartChats.forEach((c) => {
      const color = colorMap.get(c.id) ?? chatColor(0);
      config[c.id] = {
        label: `${chatLabel(c)} · ${c.user_name}`,
        color,
      };
    });
    return config;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartChats, colorMap]);

  // ── Derived: histogram ───────────────────────────────────────────────────────
  // (histogram is derived earlier alongside tokenMin/tokenMax)

  // ── Stats ────────────────────────────────────────────────────────────────────

  const totalTokens = allChats.reduce((s, c) => s + c.total_tokens, 0);
  const topChat = allChats[0] ?? null;
  const uniqueUsers = new Set(allChats.map((c) => c.user_id)).size;

  // ── Toggle helpers ───────────────────────────────────────────────────────────

  const toggleChat = (id: string) => {
    setHiddenChats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const showAll = () => setHiddenChats(new Set());
  const hideAll = () => setHiddenChats(new Set(filteredChats.map((c) => c.id)));

  // ── Loading / Error states ───────────────────────────────────────────────────

  if (loading && !data) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Token Analytics" description="Real-time token usage across chats" onBack={onBack} />
        <AnalyticsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Token Analytics" description="Real-time token usage across chats" onBack={onBack} />
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Token Analytics"
        description="Real-time token usage across chats"
        onBack={onBack}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl space-y-6 p-4 pb-12 md:p-6">

          {/* ── Controls row ─────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup
              type="single"
              value={range === "custom" ? "custom" : range}
              onValueChange={(v) => {
                if (!v) return;
                if (v !== "custom") {
                  setCustomDateRange(undefined);
                  setRange(v as Range);
                }
              }}
              className="flex-wrap"
            >
              {RANGES.map((r) => (
                <ToggleGroupItem key={r.value} value={r.value} className="px-3 text-xs">
                  {r.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            {/* ── Custom date range picker ────────────── */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={range === "custom" ? "default" : "outline"}
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <CalendarIcon className="size-3.5" />
                  {range === "custom" && customDateRange?.from
                    ? customDateRange.to
                      ? `${customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${customDateRange.to.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                      : customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "Custom"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={customDateRange}
                  onSelect={setCustomDateRange}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  captionLayout="dropdown"
                />
                <div className="flex justify-end gap-2 border-t border-border px-3 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setCustomDateRange(undefined);
                      setCalendarOpen(false);
                      setRange("1h");
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={!customDateRange?.from}
                    onClick={() => {
                      setCalendarOpen(false);
                      setRange("custom");
                      load("custom", customDateRange);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Button
              size="icon"
              variant="ghost"
              onClick={() => load(range, customDateRange)}
              disabled={loading}
              className="ml-auto h-8 w-8"
              aria-label="Refresh"
            >
              <RefreshCcwIcon className={cn("size-4", loading && "animate-spin")} />
            </Button>
          </div>

          {/* ── Stat cards ───────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total tokens" value={fmtTokens(totalTokens)} />
            <StatCard label="Active chats" value={String(allChats.length)} />
            <StatCard label="Unique users" value={String(uniqueUsers)} />
            <StatCard
              label="Top chat"
              value={topChat ? fmtTokens(topChat.total_tokens) : "—"}
              sub={topChat ? topChat.user_name : undefined}
            />
          </div>

          {/* ── Main stacked area chart ───────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Cumulative tokens over time
                {chartChats.length > 0 && (
                  <span className="ml-2 text-muted-foreground font-normal">
                    — {chartChats.length} chat{chartChats.length !== 1 ? "s" : ""} shown
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartChats.length === 0 ? (
                <div className="flex h-48 items-center justify-center">
                  <p className="text-sm text-muted-foreground">No chats match the current filter.</p>
                </div>
              ) : (
                <ChartContainer config={areaChartConfig} className="h-72 w-full">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <defs>
                      {chartChats.map((c, i) => {
                        const color = colorMap.get(c.id) ?? chatColor(i);
                        return (
                          <linearGradient key={c.id} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={color} stopOpacity={0.55} />
                            <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={fmtTokens}
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                      tickLine={false}
                      axisLine={false}
                      width={48}
                    />
                    <Tooltip
                      content={<AreaTooltip chartChats={chartChats} colorMap={colorMap} chatLabelFn={chatLabel} />}
                      cursor={{ stroke: "var(--border)", strokeWidth: 1, strokeDasharray: "4 2" }}
                    />
                    {chartChats.map((c, i) => {
                      const color = colorMap.get(c.id) ?? chatColor(i);
                      return (
                        <Area
                          key={c.id}
                          type="monotone"
                          dataKey={c.id}
                          stroke={color}
                          strokeWidth={1.5}
                          fill={`url(#grad-${i})`}
                          fillOpacity={0.25}
                          isAnimationActive={false}
                        />
                      );
                    })}
                  </AreaChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          {/* ── Histogram — click a bar to filter ───────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">
                  Token distribution
                  <span className="ml-2 text-muted-foreground font-normal">
                    ({allChats.length} chat{allChats.length !== 1 ? "s" : ""})
                  </span>
                </CardTitle>
                <div className="flex items-center gap-3">
                  {brushIndices && (
                    <span className="text-xs text-muted-foreground">
                      {fmtTokens(tokenMin)} – {tokenMax === Infinity ? "∞" : fmtTokens(tokenMax)}
                      <span className="ml-1">({filteredChats.length} chat{filteredChats.length !== 1 ? "s" : ""})</span>
                    </span>
                  )}
                  {brushIndices && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setBrushIndices(null)}
                      className="h-6 px-2 text-xs"
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{ count: { label: "Chats", color: "var(--chart-1)" } }}
                className="h-56 w-full"
              >
                <BarChart data={histogram} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 9, fill: "var(--muted-foreground)" }}
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                    allowDecimals={false}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) => [`${value} chat${Number(value) !== 1 ? "s" : ""}`, "Count"]}
                      />
                    }
                  />
                  <Bar
                    dataKey="count"
                    radius={[3, 3, 0, 0]}
                    className="cursor-pointer"
                    onClick={(data, _index) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const idx = (data as any).originalDataIndex ?? _index;
                      if (brushIndices?.[0] === idx && brushIndices?.[1] === idx) {
                        setBrushIndices(null);
                      } else {
                        setBrushIndices([idx, idx]);
                      }
                    }}
                  >
                    {histogram.map((_entry, i) => {
                      const selected = brushIndices
                        ? i >= brushIndices[0] && i <= brushIndices[1]
                        : true;
                      return (
                        <Cell
                          key={i}
                          fill={selected ? "var(--chart-1)" : "var(--muted)"}
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ChartContainer>
              {!brushIndices && (
                <p className="mt-2 text-center text-[10px] text-muted-foreground">
                  Click a bar to filter by token range
                </p>
              )}
            </CardContent>
          </Card>

          {/* ── Chat legend table ─────────────────────────────────────── */}
          {filteredChats.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    Chats in range
                    <span className="ml-2 text-muted-foreground font-normal">
                      (click row for token breakdown · click dot to toggle chart)
                    </span>
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={showAll} className="h-6 px-2 text-xs">
                      Show all
                    </Button>
                    <Button size="sm" variant="ghost" onClick={hideAll} className="h-6 px-2 text-xs">
                      Hide all
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card">
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="px-4 py-2 text-left font-normal">Chat</th>
                        <th className="px-4 py-2 text-left font-normal">Owner</th>
                        <th className="px-4 py-2 text-left font-normal">Agent</th>
                        <th className="px-4 py-2 text-left font-normal">Tools</th>
                        <th className="px-4 py-2 text-right font-normal">Msgs</th>
                        <th className="px-4 py-2 text-right font-normal">Tokens</th>
                        <th className="px-4 py-2 text-right font-normal">Est. Cost</th>
                        <th className="px-4 py-2 text-right font-normal">Updated</th>
                        <th className="px-4 py-2 text-right font-normal"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChats.map((chat, i) => {
                        const isHidden = hiddenChats.has(chat.id);
                        const color = colorMap.get(chat.id) ?? chatColor(i);
                        const inChart = i < MAX_CHART_CHATS;
                        return (
                          <tr
                            key={chat.id}
                            onClick={() => openDetail(chat)}
                            className="border-b border-border/50 transition-colors cursor-pointer hover:bg-muted/50"
                          >
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {/* Color swatch — click to toggle chart visibility */}
                                <span
                                  className={cn(
                                    "inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
                                    inChart ? "cursor-pointer" : "cursor-default",
                                    (isHidden || !inChart) && "opacity-30"
                                  )}
                                  style={{ background: color }}
                                  title={inChart ? (isHidden ? "Show on chart" : "Hide from chart") : "Not on chart (top 30 only)"}
                                  onClick={(e) => { e.stopPropagation(); if (inChart) toggleChat(chat.id); }}
                                />
                                <span
                                  className={cn(
                                    "tabular-nums",
                                    isHidden && "opacity-40"
                                  )}
                                >
                                  {chatLabel(chat)}
                                </span>
                                {!inChart && (
                                  <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0">
                                    off-chart
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              {(() => {
                                const color = userColorMap.get(chat.user_id) ?? "hsl(0,0%,50%)";
                                const bg = color.replace("hsl(", "hsla(").replace(")", ",0.12)");
                                const border = color.replace("hsl(", "hsla(").replace(")", ",0.35)");
                                return (
                                  <div>
                                    <span
                                      className={cn("inline-block font-medium px-1.5 py-0.5 rounded text-xs", isHidden && "opacity-40")}
                                      style={{ color, backgroundColor: bg, border: `1px solid ${border}` }}
                                    >
                                      {chat.user_name}
                                    </span>
                                    {chat.user_email && (
                                      <div className="text-muted-foreground opacity-70 truncate max-w-45">
                                        {chat.user_email}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-2">
                              <span className={cn("text-muted-foreground", isHidden && "opacity-40")}>
                                {chat.agent_name ?? <span className="opacity-40">—</span>}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {(chat.tool_names ?? []).length > 0 ? (
                                <TooltipProvider delayDuration={100}>
                                  <div className={cn("flex items-center gap-1", isHidden && "opacity-40")}>
                                    {(chat.tool_names ?? []).map((t) => {
                                      const { Icon, color, label } = toolIconDef(t);
                                      return (
                                        <UITooltip key={t}>
                                          <TooltipTrigger asChild>
                                            <span className="inline-flex">
                                              <Icon className={cn("size-3.5", color)} />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="text-xs">
                                            {label}
                                          </TooltipContent>
                                        </UITooltip>
                                      );
                                    })}
                                  </div>
                                </TooltipProvider>
                              ) : (
                                <span className="opacity-40">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              <span className={cn("text-muted-foreground", isHidden && "opacity-40")}>
                                {chat.message_count}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              <TooltipProvider delayDuration={100}>
                                <UITooltip>
                                  <TooltipTrigger asChild>
                                    <span className={cn("cursor-default", isHidden && "opacity-40")}>
                                      <span className="font-medium">{fmtTokens(chat.total_tokens)}</span>
                                      {(chat.input_tokens > 0 || chat.output_tokens > 0) && (
                                        <div className="text-[10px] text-muted-foreground">
                                          {fmtTokens(chat.input_tokens)} in · {fmtTokens(chat.output_tokens)} out
                                        </div>
                                      )}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs">
                                    <div className="space-y-0.5">
                                      <div className="flex gap-3 justify-between"><span className="text-muted-foreground">Input</span><span>{chat.input_tokens.toLocaleString()}</span></div>
                                      <div className="flex gap-3 justify-between"><span className="text-muted-foreground">Output</span><span>{chat.output_tokens.toLocaleString()}</span></div>
                                      <div className="flex gap-3 justify-between"><span className="text-muted-foreground">Cache read</span><span>{chat.cache_read_tokens.toLocaleString()}</span></div>
                                      <div className="flex gap-3 justify-between"><span className="text-muted-foreground">Cache write</span><span>{chat.cache_write_tokens.toLocaleString()}</span></div>
                                    </div>
                                  </TooltipContent>
                                </UITooltip>
                              </TooltipProvider>
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {(() => {
                                const cost = chatCost(chat);
                                if (cost === null) return <span className="opacity-30 text-muted-foreground">—</span>;
                                return (
                                  <span className={cn("text-muted-foreground", isHidden && "opacity-40")}>
                                    {fmtCost(cost)}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-2 text-right text-muted-foreground">
                              {new Date(chat.updated_at).toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                                day: "numeric",
                                month: "short",
                              })}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                title="Reliability analysis"
                                onClick={(e) => { e.stopPropagation(); runAnalysis(chat); }}
                              >
                                <SparklesIcon className="size-3.5 text-muted-foreground" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {allChats.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16">
              <p className="text-sm text-muted-foreground">No chats found in the last {RANGES.find((r) => r.value === range)?.label}.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Message token detail drawer ─────────────────────────────── */}
      <Drawer open={detailOpen} onOpenChange={setDetailOpen} direction="bottom">
        <DrawerContent>
          <DrawerHeader className="shrink-0 border-b border-border pb-3">
            <DrawerTitle className="text-sm">Token Breakdown</DrawerTitle>
            <DrawerDescription className="text-xs">
              {detailChat
                ? `${chatLabel(detailChat)} · ${detailChat.user_name} · ${detailChat.message_count} message${detailChat.message_count !== 1 ? "s" : ""}`
                : "Assistant message token usage per turn"}
            </DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 sm:px-6 py-5">
            {detailLoading && (
              <ContentSkeleton />
            )}
            {!detailLoading && detailMessages.length === 0 && (
              <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
                No token usage data found for this chat.
              </div>
            )}
            {!detailLoading && detailMessages.length > 0 && (
              <div className="space-y-5">
                {/* Summary tiles */}
                {detailChat && (
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: "Input",       value: detailChat.input_tokens,       color: "var(--chart-1)" },
                      { label: "Output",      value: detailChat.output_tokens,      color: "var(--chart-2)" },
                      { label: "Cache read",  value: detailChat.cache_read_tokens,  color: "var(--chart-3)" },
                      { label: "Cache write", value: detailChat.cache_write_tokens, color: "var(--chart-4)" },
                    ].map(({ label, value, color: c }) => (
                      <div key={label} className="rounded-md border border-border bg-muted/30 px-3 py-2 text-center">
                        <p className="text-[10px] text-muted-foreground">{label}</p>
                        <p className="mt-0.5 text-base font-semibold tabular-nums" style={{ color: c }}>{fmtTokens(value)}</p>
                      </div>
                    ))}
                  </div>
                )}
                {/* Single composite area chart */}
                <CompositeTokenChart data={detailMessages} />
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* ── Reliability analysis sheet ──────────────────────────────── */}
      <Sheet open={analysisOpen} onOpenChange={(o) => { if (!o) analysisAbortRef.current?.abort(); setAnalysisOpen(o); }}>
        <SheetContent
          side="right"
          className="flex flex-col gap-0 p-0 transition-none"
          style={{ width: sheetWidth, maxWidth: "none" }}
        >
          {/* Drag-to-resize handle */}
          <div
            className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
          />
          <SheetHeader className="shrink-0 border-b border-border px-6 pt-4 pb-3">
            <div className="flex items-center gap-2 pr-8">
              <SparklesIcon className="size-4 shrink-0 text-muted-foreground" />
              <SheetTitle className="text-sm">Reliability Analysis</SheetTitle>
            </div>
            <SheetDescription className="text-xs">
              {analysisChat
                ? `${chatLabel(analysisChat)} · ${analysisChat.user_name} · ${fmtTokens(analysisChat.total_tokens)} tokens`
                : "Structural analysis only — no chat content is read."}
            </SheetDescription>
            <div className="flex items-center gap-1.5 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyAnalysis}
                disabled={!analysisText || analysisLoading}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownloadAnalysis}
                disabled={!analysisText || analysisLoading}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                <DownloadIcon className="size-3" />
                Download .md
              </Button>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
            {analysisLoading && analysisText === "" && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <SparklesIcon className="size-3.5 animate-pulse" />
                Analysing…
              </div>
            )}
            {analysisError && (
              <p className="text-xs text-destructive">{analysisError}</p>
            )}
            {analysisText && (
              <MessageResponse isAnimating={analysisLoading}>
                {analysisText}
              </MessageResponse>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground truncate">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── AnalysisMarkdown ─────────────────────────────────────────────────────────
// Very lightweight renderer for the structured Haiku output (h2 + bullets + bold)

function _AnalysisMarkdown({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1 text-xs leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith("## ")) {
          return (
            <p key={i} className="mt-4 mb-1 font-semibold text-foreground text-sm first:mt-0">
              {line.slice(3)}
            </p>
          );
        }
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return (
            <p key={i} className="pl-3 text-muted-foreground before:content-['•'] before:mr-2 before:text-foreground">
              <InlineMarkdown text={line.slice(2)} />
            </p>
          );
        }
        if (line.trim() === "") return <div key={i} className="h-1" />;
        return (
          <p key={i} className="text-muted-foreground">
            <InlineMarkdown text={line} />
          </p>
        );
      })}
    </div>
  );
}

function InlineMarkdown({ text }: { text: string }) {
  // Render **bold** spans
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="text-foreground font-medium">{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// ─── CompositeTooltip ────────────────────────────────────────────────────────

function CompositeTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card shadow-lg text-xs min-w-44">
      <div className="border-b border-border px-3 py-2 font-medium text-foreground">
        {label}
      </div>
      <div className="space-y-1.5 px-3 py-2">
        {payload.map((item) => {
          const cfg = COMPOSITE_CONFIG[item.dataKey as keyof typeof COMPOSITE_CONFIG];
          return (
            <div key={item.dataKey} className="flex items-center gap-2">
              <span
                className="h-2 w-2 shrink-0 rounded-sm"
                style={{ background: item.color }}
              />
              <span className="flex-1 text-muted-foreground">{cfg?.label ?? item.dataKey}</span>
              <span className="tabular-nums font-medium text-foreground">
                {fmtTokens(item.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CompositeTokenChart ──────────────────────────────────────────────────────

const COMPOSITE_CONFIG: ChartConfig = {
  input:       { label: "Input",       color: "var(--chart-1)" },
  output:      { label: "Output",      color: "var(--chart-2)" },
  cache_read:  { label: "Cache read",  color: "var(--chart-3)" },
  cache_write: { label: "Cache write", color: "var(--chart-4)" },
};

function CompositeTokenChart({ data }: { data: MessageTokenPoint[] }) {
  const interval = data.length > 20 ? Math.floor(data.length / 10) : 0;
  return (
    <ChartContainer config={COMPOSITE_CONFIG} className="h-64 w-full">
      <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <ChartLegend content={<ChartLegendContent />} />
        <defs>
          {(["input", "output", "cache_read", "cache_write"] as const).map((key, i) => (
            <linearGradient key={key} id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={`var(--chart-${i + 1})`} stopOpacity={0.25} />
              <stop offset="95%" stopColor={`var(--chart-${i + 1})`} stopOpacity={0.03} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
          tickLine={false}
          axisLine={false}
          interval={interval}
        />
        <YAxis
          tickFormatter={fmtTokens}
          tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <ChartTooltip
          content={<CompositeTooltip />}
          cursor={{ stroke: "var(--border)", strokeWidth: 1, strokeDasharray: "4 2" }}
        />
        {(["input", "output", "cache_read", "cache_write"] as const).map((key, i) => (
          <Area
            key={key}
            dataKey={key}
            type="monotone"
            stroke={`var(--chart-${i + 1})`}
            strokeWidth={1.5}
            fill={`url(#fill-${key})`}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
}

// ─── AreaTooltip ──────────────────────────────────────────────────────────────

type TooltipPayloadItem = {
  dataKey: string;
  value: number;
  payload: Record<string, number | string>;
};

function AreaTooltip({
  active,
  payload,
  label,
  chartChats,
  colorMap,
  chatLabelFn,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  chartChats: TokenChat[];
  colorMap: Map<string, string>;
  chatLabelFn: (c: TokenChat) => string;
}) {
  if (!active || !payload?.length) return null;

  // Sort entries descending by value, filter out zeros
  const items = payload
    .map((p) => {
      const chat = chartChats.find((c) => c.id === p.dataKey);
      return { value: p.value, chat, color: chat ? (colorMap.get(chat.id) ?? "var(--muted-foreground)") : "var(--muted-foreground)" };
    })
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = items.reduce((s, i) => s + i.value, 0);

  return (
    <div className="rounded-lg border border-border bg-card shadow-lg text-xs min-w-52 max-w-72">
      {/* Header: time + total */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-foreground tabular-nums">{fmtTokens(total)}</span>
      </div>
      {/* Rows */}
      <div className="px-3 py-2 space-y-1.5 max-h-64 overflow-y-auto">
        {items.map(({ value, chat, color }) => (
          <div key={chat?.id ?? `item-${color}`} className="flex items-center gap-2">
            <span
              className="shrink-0 h-2 w-2 rounded-full"
              style={{ background: color }}
            />
            <span className="flex-1 truncate text-muted-foreground">
              {chat ? chatLabelFn(chat) : "—"}
            </span>
            {chat && (
              <span className="shrink-0 text-muted-foreground opacity-70 text-[10px]">
                {chat.user_name}
              </span>
            )}
            <span className="shrink-0 tabular-nums font-medium text-foreground">
              {fmtTokens(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
